import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, timeout } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Nomenclature {
  id: number;
  numero_nomenclature: number;
  nom: string;
  date_creation: string;
}

export interface NomenclatureComposant {
  nomenclature_id: number;
  composant_id: number;
  quantite: number;
}

export interface NomenclaturePrixHt {
  nomenclature_id: number;
  prix_ht: number;
  devise: string;
}

export interface NomenclatureCreatePayload {
  numero_nomenclature: number;
  nom: string;
  date_creation?: string;
  source_nomenclature_id?: number;
}

@Injectable({ providedIn: 'root' })
export class NomenclaturesService {
  private readonly baseUrl = `${environment.apiBaseUrl}/api/nomenclatures`;
  private readonly requestTimeoutMs = 30000;

  constructor(private readonly http: HttpClient) {}

  list(): Observable<Nomenclature[]> {
    return this.http
      .get<{ items?: Nomenclature[] } | Nomenclature[]>(this.baseUrl)
      .pipe(
        timeout(this.requestTimeoutMs),
        map((response) => {
          if (Array.isArray(response)) {
            return response;
          }
          return Array.isArray(response?.items) ? response.items : [];
        })
      );
  }

  getById(id: number): Observable<Nomenclature> {
    return this.http
      .get<Nomenclature>(`${this.baseUrl}/${id}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  create(payload: NomenclatureCreatePayload): Observable<Nomenclature> {
    return this.http
      .post<Nomenclature>(this.baseUrl, payload)
      .pipe(timeout(this.requestTimeoutMs));
  }

  update(id: number, payload: NomenclatureCreatePayload): Observable<Nomenclature> {
    return this.http
      .put<Nomenclature>(`${this.baseUrl}/${id}`, payload)
      .pipe(timeout(this.requestTimeoutMs));
  }

  delete(id: number): Observable<{ deleted: boolean; id: number }> {
    return this.http
      .delete<{ deleted: boolean; id: number }>(`${this.baseUrl}/${id}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  getComposants(nomenclatureId: number): Observable<NomenclatureComposant[]> {
    return this.http
      .get<{ items?: NomenclatureComposant[] } | NomenclatureComposant[]>(`${this.baseUrl}/${nomenclatureId}/composants`)
      .pipe(
        timeout(this.requestTimeoutMs),
        map((response) => {
          if (Array.isArray(response)) {
            return response;
          }
          return Array.isArray(response?.items) ? response.items : [];
        })
      );
  }

  getPrixHt(nomenclatureId: number): Observable<NomenclaturePrixHt> {
    return this.http
      .get<NomenclaturePrixHt>(`${this.baseUrl}/${nomenclatureId}/prix-ht`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  addComposant(nomenclatureId: number, composantId: number, quantite: number): Observable<NomenclatureComposant> {
    return this.http
      .post<NomenclatureComposant>(`${this.baseUrl}/${nomenclatureId}/composants`, { composant_id: composantId, quantite })
      .pipe(timeout(this.requestTimeoutMs));
  }

  removeComposant(nomenclatureId: number, composantId: number): Observable<{ deleted: boolean }> {
    return this.http
      .delete<{ deleted: boolean }>(`${this.baseUrl}/${nomenclatureId}/composants/${composantId}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  updateComposant(nomenclatureId: number, composantId: number, quantite: number): Observable<NomenclatureComposant> {
    return this.http
      .put<NomenclatureComposant>(`${this.baseUrl}/${nomenclatureId}/composants/${composantId}`, { quantite })
      .pipe(timeout(this.requestTimeoutMs));
  }
}
